import React, { Component, useEffect } from "react";

import {
  Button,
  Checkbox,
  Col,
  Drawer,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Table,
  Tooltip,
} from "antd";
import type { FormInstance } from "antd/es/form";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { InfoCircleOutlined, RightOutlined } from "@ant-design/icons";

import { capitalizeString, groupAndSortByKey } from "common/utils/helpers";
import { useDrawerScrollMargin } from "common/utils/useScrollPosition";
import { getUserDashboardSelection } from "common/utils/userDashboard";
const { Option } = Select;

type PermissionAction = "read" | "create" | "edit" | "delete" | string;

interface CustomerApplication {
  _id: string;
  name: string;
  active?: boolean;
}

interface Dashboard {
  id: string | number;
  _id?: string | number;
  name: string;
  customerApplication?: string | number;
}

interface RolePermission {
  permissionId: string;
  read?: boolean;
  create?: boolean;
  edit?: boolean;
  delete?: boolean;
  [key: string]: any;
}

interface Role {
  id: string | number;
  _id?: string | number;
  name: string;
  description?: string;
  customerApplication?: string | number;
  isActive?: boolean;
  dashboards?: (string | number)[];
  permissions?: RolePermission[];
  [key: string]: any;
}

interface Permission {
  id: string;
  name: string;
  description?: string;
  customerApplication?: string | number;
  supportsRead?: boolean;
  supportsCreate?: boolean;
  supportsEdit?: boolean;
  supportsDelete?: boolean;
  [key: string]: any;
}

interface EditingRow {
  firstName?: string;
  lastName?: string;
  email?: string;
  admin?: boolean;
  vendorManager?: boolean;
  isActive?: boolean;
  customerApplications: CustomerApplication[];
  roles: Role[];
  dashboards?: (string | number)[];
  userLevelPermissions: any[];
  [key: string]: any;
}

interface EditingStateProp {
  editingRow: EditingRow;
  userLevelPermissions: any[];
}

interface EditUserHandlers {
  closeEditDrawer: (e?: any) => void;
  onFinishEditing: (values: any) => boolean | Promise<boolean>;
}

interface EditUserValues {
  visibleEditDrawer: boolean;
  editingForm: FormInstance<any>;
  applicationsList: CustomerApplication[];
  rolesList: Role[];
  permissionsList: Permission[];
  dashboardsList: Dashboard[];
  editing: EditingStateProp;
  editFormRef: React.MutableRefObject<FormInstance<any> | null>;
  languageCodes: { title: string; value: string }[];
  canManagePrivileges: boolean;
}

interface EditUserProps {
  handlers: EditUserHandlers;
  values: EditUserValues;
}

interface EditUserState {
  selectedCA: Set<string>;
  selectedRoles: Set<string | number>;
  selectedPermissions: Map<
    string | number,
    {
      [action: string]: boolean;
    }
  >;
  displayedDashboards: Dashboard[];
}

export const submitEditUser = async (
  values: any,
  onFinishEditing: EditUserHandlers["onFinishEditing"],
  closeEditDrawer: () => void
) => {
  if (await onFinishEditing(values)) {
    closeEditDrawer();
  }
};

const normalizeId = (value: any): string =>
  String(
    typeof value === "string" || typeof value === "number"
      ? value
      : value?._id || value?.id || value?.value || value?.key || value
  );

const getDashboardId = (dashboard: any): string =>
  String(
    typeof dashboard === "string" || typeof dashboard === "number"
      ? dashboard
      : dashboard?._id || dashboard?.id
  );

class EditUser extends Component<EditUserProps, EditUserState> {
  state: EditUserState = {
    selectedCA: new Set(
      this.props.values.editing.editingRow.customerApplications.map(
        (ca) => normalizeId(ca)
      )
    ),
    selectedRoles: new Set(
      this.props.values.editing.editingRow.roles.map((role) => normalizeId(role))
    ),
    selectedPermissions: new Map(),
    displayedDashboards: this.props.values.dashboardsList.filter(
      (dashboard) => {
        const customerApplications =
          this.props.values.editing.editingRow.customerApplications;

        return customerApplications.some(
          (customerApplication) =>
            normalizeId(customerApplication) === normalizeId(dashboard.customerApplication)
        );
      }
    ),
  };

  /**
   * Returns [value, roleDisabled, matchedRoles]
   */
  permissionValue = (
    id: string | number,
    key: PermissionAction
  ): [boolean | undefined, boolean, Role[]] => {
    const { selectedRoles, selectedPermissions } = this.state;
    const { rolesList } = this.props.values;

    let perm: boolean | undefined;
    let roleDisabled = false;
    const matchedRoles: Role[] = [];

    Array.from(selectedRoles).forEach((rid) => {
      if (perm) {
        return;
      }
      const role = rolesList.find((r) => normalizeId(r) === normalizeId(rid));
      if (!role || !role.permissions) return;

      role.permissions.forEach((p) => {
        if (p.permissionId === id) {
          const v = (p as any)[key];
          perm = v;
          roleDisabled = !!v;
          if (roleDisabled) {
            matchedRoles.push(role);
          }
        }
      });
    });

    if (!perm) {
      if (
        selectedPermissions &&
        selectedPermissions instanceof Map &&
        selectedPermissions.get(id) &&
        selectedPermissions.get(id)![key] !== undefined
      ) {
        perm = selectedPermissions.get(id)![key];
      }
    }

    return [perm, roleDisabled, matchedRoles];
  };

  private _isMounted = false;
  
  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  closeDrawer = (closeEditDrawer: (e?: any) => void, e?: any) => {
    closeEditDrawer(e);
    if (!this._isMounted) return;
    this.setState({
      selectedCA: new Set(),
      selectedRoles: new Set(),
      selectedPermissions: new Map(),
      displayedDashboards: [],
    });
  };

  handleChangeApplications = (e: (string | number)[]) => {
    const { rolesList, dashboardsList, editingForm, editFormRef } =
      this.props.values;

    const normalizedApps = (e || []).map((id) => normalizeId(id));
    this.setState({ selectedCA: new Set(normalizedApps as string[]) });

    const rolesWithDetails: Role[] = [];

    Array.from(this.state.selectedRoles).forEach((rid) => {
      const role = rolesList.find((r) => normalizeId(r) === normalizeId(rid));
      if (role) rolesWithDetails.push(role);
    });

    const newRoles = rolesWithDetails
      .filter((role) => normalizedApps.includes(normalizeId(role.customerApplication)))
      .map((role) => normalizeId(role));

    this.setState({ selectedRoles: new Set(newRoles) });

    if (editFormRef.current) {
      editingForm.setFieldsValue({
        rolesValue: newRoles,
      });
    }

    const filteredDashboardsList = dashboardsList.filter((dashboard) =>
      normalizedApps.includes(normalizeId(dashboard.customerApplication))
    );

    const formValues = editingForm.getFieldsValue();
    editingForm.setFieldsValue({
      // only keep selected dashboards that still exist in filtered options
      dashboardsValue:
        formValues?.dashboardsValue?.filter((dashboardId: any) =>
          filteredDashboardsList.some(
            (dashboardListItem) => getDashboardId(dashboardListItem) === getDashboardId(dashboardId)
          )
        ) || [],
    });

    this.setState({ displayedDashboards: filteredDashboardsList });
  };

  handleChangeRoles = (selectedRoleIds: (string | number)[]) => {
    const { rolesList, editingForm, dashboardsList } = this.props.values;
    const prevSelectedRoles = this.state.selectedRoles || new Set();
    const newSelectedRoles = new Set(selectedRoleIds || []);

    const prevRoleIds = new Set(Array.from(prevSelectedRoles).map(String));
    const newRoleIds = new Set(Array.from(newSelectedRoles).map(String));

    const removedRoleIds = Array.from(prevRoleIds).filter((rid) => !newRoleIds.has(rid));
    const addedRoleIds = Array.from(newRoleIds).filter((rid) => !prevRoleIds.has(rid));

    const findRoleById = (rid: string | number) =>
      rolesList.find((r: any) => String(r._id) === String(rid) || String(r.id) === String(rid));

    const actions: PermissionAction[] = ["read", "create", "edit", "delete"];

    const remainingRolePermissions = new Map<string, { [action: string]: boolean }>();
    Array.from(newRoleIds).forEach((rid) => {
      const role = findRoleById(rid);
      if (!role || !role.permissions) return;
      role.permissions.forEach((p: any) => {
        if (!remainingRolePermissions.has(p.permissionId)) {
          remainingRolePermissions.set(p.permissionId, {});
        }
        const entry = remainingRolePermissions.get(p.permissionId)!;
        actions.forEach((a) => {
          if ((p as any)[a]) entry[a] = true;
        });
      });
    });

    const updatedSelectedPermissions = new Map(this.state.selectedPermissions);
    const formUpdates: Record<string, any> = {};

    removedRoleIds.forEach((rid) => {
      const role = findRoleById(rid);
      if (!role || !role.permissions) return;

      role.permissions.forEach((p: any) => {
        const pid = p.permissionId;
        actions.forEach((a) => {
          if (p[a]) {
            const remainingProvides =
              remainingRolePermissions.get(pid) && remainingRolePermissions.get(pid)![a];

            if (!remainingProvides) {
              const existing = updatedSelectedPermissions.get(pid) || {};
              updatedSelectedPermissions.set(pid, { ...existing, [a]: false });

              formUpdates[`permissions~${a}-${pid}`] = false;
              formUpdates[`role-permissions~${a}-${pid}`] = false;
            }
          }
        });
      });
    });

    addedRoleIds.forEach((rid) => {
      const role = findRoleById(rid);
      if (!role || !role.permissions) return;

      role.permissions.forEach((p: any) => {
        const pid = p.permissionId;
        actions.forEach((a) => {
          if (p[a]) {
            const existing = updatedSelectedPermissions.get(pid) || {};
            if (existing[a] !== undefined) {
              const cleaned = { ...existing };
              delete cleaned[a];
              if (Object.keys(cleaned).length === 0) {
                updatedSelectedPermissions.delete(pid);
              } else {
                updatedSelectedPermissions.set(pid, cleaned);
              }
            }
            formUpdates[`role-permissions~${a}-${pid}`] = true;
            formUpdates[`permissions~${a}-${pid}`] = undefined;
          }
        });
      });
    });

    if (editingForm && Object.keys(formUpdates).length > 0) {
      editingForm.setFieldsValue(formUpdates);
    }

    const dashboardsFromRoles: (string | number)[] = [];
    Array.from(newRoleIds).forEach((rid) => {
      const role = findRoleById(rid);
      if (role?.dashboards && Array.isArray(role.dashboards)) {
        role.dashboards.forEach((d: any) => {
          const dashboardId = getDashboardId(d);
          if (!dashboardsFromRoles.includes(dashboardId)) dashboardsFromRoles.push(dashboardId);
        });
      }
    });

    const filteredDashboardsList = dashboardsList.filter((dashboard: any) =>
      Array.from(this.state.selectedCA || new Set()).includes(normalizeId(dashboard.customerApplication))
    );

    // ignore stale role dashboard ids that no longer exist in the selectable dashboard list
    const validRoleDashboardIds = dashboardsFromRoles.filter((dashboardId) =>
      filteredDashboardsList.some(
        (dashboard: any) => getDashboardId(dashboard) === getDashboardId(dashboardId)
      )
    );

    const finalDashboards =
      validRoleDashboardIds.length > 0
        ? dashboardsList.filter((dashboard: any) =>
          validRoleDashboardIds.includes(getDashboardId(dashboard))
        )
        : filteredDashboardsList;

    if (editingForm) {
      if (validRoleDashboardIds.length > 0) {
        editingForm.setFieldsValue({ dashboardsValue: validRoleDashboardIds });
      } else {
        editingForm.setFieldsValue({ dashboardsValue: [] });
      }
    }

    this.setState({
      displayedDashboards: finalDashboards,
      selectedRoles: newSelectedRoles,
      selectedPermissions: updatedSelectedPermissions,
    });
  };


  onChangePermission = (e: CheckboxChangeEvent, id: string | number) => {
    const { selectedPermissions } = this.state;

    const name = e.target.name as string;
    const checked = e.target.checked;

    if (selectedPermissions.has(id)) {
      const p = selectedPermissions.get(id);
      selectedPermissions.set(id, { ...(p || {}), [name]: checked });
    } else {
      selectedPermissions.set(id, { [name]: checked });
    }

    this.setState({ selectedPermissions });
  };

  columns = [
    {
      title: "",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "",
      dataIndex: "description",
      key: "description",
      width: 10,
      render: (_text: any, record: Permission) => (
        <Tooltip
          title={record.description}
          color={"var(--tertiary-color)"}
          placement={"left"}
        >
          <InfoCircleOutlined style={{ color: "var(--tertiary-color)" }} />
        </Tooltip>
      ),
    },
    {
      title: "Read",
      dataIndex: "read",
      key: "read",
      width: 60,
      render: (_text: any, record: Permission) => {
        const [val, roleDisabled, matchedRoles] = this.permissionValue(
          record.id,
          "read"
        ),
          displayedRoles = matchedRoles
            ?.map((role) => capitalizeString(role.name))
            .join(", ");

        return record.supportsRead ? (
          <Tooltip
            placement={"left"}
            title={
              roleDisabled
                ? matchedRoles.length > 1
                  ? `This permission is part of the following roles: ${displayedRoles}. Remove roles to allow changing permission.`
                  : `This permission is part of the following role: ${displayedRoles}. Remove role to allow changing permission.`
                : ""
            }
          >
            <div>
              <Form.Item
                name={`${roleDisabled ? "role-" : ""}permissions~read-${record.id}`}
                key={`permissions~read-${record.id}`}
                noStyle
                valuePropName="checked"
                initialValue={roleDisabled ? val : undefined}
              >
                <Checkbox
                  name="read"
                  disabled={roleDisabled || !this.props.values.canManagePrivileges}
                />
              </Form.Item>
            </div>
          </Tooltip>
        ) : (
          <Form.Item
            name={`permissions~read-${record.id}`}
            key={`permissions~read-${record.id}`}
            noStyle
            valuePropName="checked"
          >
            <Tooltip
              title={`This permission does not support this change.`}
              placement={"left"}
            >
              <Checkbox name="read" disabled />
            </Tooltip>
          </Form.Item>
        );
      },
    },
    {
      title: "Create",
      dataIndex: "create",
      key: "create",
      width: 60,
      render: (_text: any, record: Permission) => {
        const [val, roleDisabled, matchedRoles] = this.permissionValue(
          record.id,
          "create"
        ),
          displayedRoles = matchedRoles
            ?.map((role) => capitalizeString(role.name))
            .join(", ");
        return record.supportsCreate ? (
          <Tooltip
            placement={"left"}
            title={
              roleDisabled
                ? matchedRoles.length > 1
                  ? `This permission is part of the following roles: ${displayedRoles}. Remove roles to allow changing permission.`
                  : `This permission is part of the following role: ${displayedRoles}. Remove role to allow changing permission.`
                : ""
            }
          >
            <div>
              <Form.Item
                name={`${roleDisabled ? "role-" : ""}permissions~create-${record.id}`}
                key={`permissions~create-${record.id}`}
                noStyle
                valuePropName="checked"
                initialValue={roleDisabled ? val : undefined}
              >
                <Checkbox
                  name="create"
                  disabled={roleDisabled || !this.props.values.canManagePrivileges}
                />
              </Form.Item>
            </div>
          </Tooltip>
        ) : (
          <Form.Item
            name={`permissions~create-${record.id}`}
            key={`permissions~create-${record.id}`}
            noStyle
            valuePropName="checked"
          >
            <Tooltip
              title={`This permission does not support this change.`}
              placement={"left"}
            >
              <Checkbox name="create" disabled />
            </Tooltip>
          </Form.Item>
        );
      },
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      width: 60,
      render: (_text: any, record: Permission) => {
        const [val, roleDisabled, matchedRoles] = this.permissionValue(
          record.id,
          "edit"
        ),
          displayedRoles = matchedRoles
            ?.map((role) => capitalizeString(role.name))
            .join(", ");
        return record.supportsEdit ? (
          <Tooltip
            placement={"left"}
            title={
              roleDisabled
                ? matchedRoles.length > 1
                  ? `This permission is part of the following roles: ${displayedRoles}. Remove roles to allow changing permission.`
                  : `This permission is part of the following role: ${displayedRoles}. Remove role to allow changing permission.`
                : ""
            }
          >
            <div>
              <Form.Item
                name={`${roleDisabled ? "role-" : ""}permissions~edit-${record.id}`}
                key={`permissions~edit-${record.id}`}
                noStyle
                valuePropName="checked"
                initialValue={roleDisabled ? val : undefined}
              >
                <Checkbox
                  name="edit"
                  disabled={roleDisabled || !this.props.values.canManagePrivileges}
                />
              </Form.Item>
            </div>
          </Tooltip>
        ) : (
          <Form.Item
            name={`permissions~edit-${record.id}`}
            key={`permissions~edit-${record.id}`}
            noStyle
            valuePropName="checked"
          >
            <Tooltip
              title={`This permission does not support this change.`}
              placement={"left"}
            >
              <Checkbox name="edit" disabled />
            </Tooltip>
          </Form.Item>
        );
      },
    },
    {
      title: "Delete",
      dataIndex: "delete",
      key: "delete",
      width: 60,
      render: (_text: any, record: Permission) => {
        const [val, roleDisabled, matchedRoles] = this.permissionValue(
          record.id,
          "delete"
        ),
          displayedRoles = matchedRoles
            ?.map((role) => capitalizeString(role.name))
            .join(", ");
        return record.supportsDelete ? (
          <Tooltip
            placement={"left"}
            title={
              roleDisabled
                ? matchedRoles.length > 1
                  ? `This permission is part of the following roles: ${displayedRoles}. Remove roles to allow changing permission.`
                  : `This permission is part of the following role: ${displayedRoles}. Remove role to allow changing permission.`
                : ""
            }
          >
            <div>
              <Form.Item
                name={`${roleDisabled ? "role-" : ""}permissions~delete-${record.id}`}
                key={`permissions~delete-${record.id}`}
                noStyle
                valuePropName="checked"
                initialValue={roleDisabled ? val : undefined}
              >
                <Checkbox
                  name="delete"
                  disabled={roleDisabled || !this.props.values.canManagePrivileges}
                />
              </Form.Item>
            </div>
          </Tooltip>
        ) : (
          <Form.Item
            name={`permissions~delete-${record.id}`}
            key={`permissions~delete-${record.id}`}
            noStyle
            valuePropName="checked"
          >
            <Tooltip
              title={`This permission does not support this change.`}
              placement={"left"}
            >
              <Checkbox name="delete" disabled />
            </Tooltip>
          </Form.Item>
        );
      },
    },
  ];

  render() {
    const {
      visibleEditDrawer,
      editingForm,
      applicationsList,
      rolesList,
      permissionsList,
      dashboardsList,
      editing,
      editFormRef,
      languageCodes,
    } = this.props.values;
    const { closeEditDrawer, onFinishEditing } = this.props.handlers;
    const { selectedCA, displayedDashboards } = this.state;

    const editingRow = editing && editing.editingRow;
    const userInitialPermissions = editingRow.userLevelPermissions || [];

    const permissionMap = new Map<string | number, Permission[]>();

    permissionsList.forEach((permission) => {
      if (!permission.customerApplication) {
        return;
      }

      if (!permissionMap.has(permission.customerApplication)) {
        permissionMap.set(permission.customerApplication, []);
      }

      const arr = permissionMap.get(permission.customerApplication)!;

      arr.push(permission);
      permissionMap.set(permission.customerApplication, arr);
    });

    const dashboardSelection = getUserDashboardSelection({
      row: editingRow,
      rolesList,
      dashboardsList,
    });

    let initialFormValues: any = {
      firstNameValue: editingRow?.firstName,
      lastNameValue: editingRow?.lastName,
      emailValue: editingRow?.email,

      customerApplicationsValue: editingRow.customerApplications.map(
        (ca: CustomerApplication) => normalizeId(ca)
      ),
      rolesValue: (editingRow.roles || []).map((role: Role) => normalizeId(role)),
      dashboardsValue: dashboardSelection.dashboardIds,
      adminValue: editingRow.admin,
      vendorManagerValue: editingRow.vendorManager,
      isActiveValue: editingRow.isActive,
    };

    const validDashboardIds = new Set(
      (displayedDashboards || []).map((dashboard: any) => getDashboardId(dashboard))
    );

    initialFormValues.dashboardsValue = (initialFormValues.dashboardsValue || []).filter(
      (dashboardId: any) => validDashboardIds.has(getDashboardId(dashboardId))
    );

    userInitialPermissions.forEach((dp: any) => {
      for (const [key, value] of Object.entries(dp)) {
        if (value === true) {
          initialFormValues[`permissions~${key}-${dp.permissionId}`] = value;
        }
      }
    });

    return (
      <Drawer
        title="Edit User"
        placement="right"
        onClose={(e) => this.closeDrawer(closeEditDrawer, e)}
        open={visibleEditDrawer}
        width={550}
        closable
        className="manage-pages-drawer edit-user-drawer"
        forceRender
        rootClassName="ant-drawer-open"
      >
        <Form
          form={editingForm}
          ref={editFormRef as any}
          layout="vertical"
          onFinish={(values) => submitEditUser(
            values,
            onFinishEditing,
            () => this.closeDrawer(closeEditDrawer)
          )}
          name="editUserForm"
          className="edit-user-form"
          initialValues={initialFormValues}
        >
          <Form.Item
            name="firstNameValue"
            label="First Name"
            rules={[{ required: true, message: "Please type the first name" }]}
          >
            <Input placeholder="Ex: John" />
          </Form.Item>
          <Form.Item
            name="lastNameValue"
            label="Last Name"
            rules={[{ required: true, message: "Please type the last name" }]}
          >
            <Input placeholder="Ex: Smith" />
          </Form.Item>
          <Form.Item
            name="emailValue"
            label="Email"
            rules={[
              { required: true, message: "Please type the email" },
              { type: "email", message: "The email is not valid." },
            ]}
          >
            <Input placeholder="Ex: john@rws.com" />
          </Form.Item>
          <Form.Item name="targetLanguagesValue" label="Target Languages">
            <Select
              mode="multiple"
              allowClear
              placeholder="Select target languages"
              style={{ width: "100%" }}
              showSearch
              options={languageCodes.map((opt) => ({
                label: opt.title,
                value: opt.value,
              }))}
            />
          </Form.Item>
          <Row>
            <Col span={8}>
              <Form.Item
                label="Admin"
                name="adminValue"
                valuePropName="checked"
              >
                <Switch
                  defaultChecked={editingRow.admin}
                  disabled={!this.props.values.canManagePrivileges}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Vendor Manager"
                name="vendorManagerValue"
                valuePropName="checked"
              >
                <Switch
                  defaultChecked={editingRow.vendorManager}
                  disabled={!this.props.values.canManagePrivileges}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Active"
                name="isActiveValue"
                valuePropName="checked"
              >
                <Switch defaultChecked={editingRow.isActive} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Customer Applications"
            name="customerApplicationsValue"
            rules={[
              {
                required: true,
                message: "Please select customer application",
              },
            ]}
          >
            <Select
              placeholder="Select application"
              onChange={this.handleChangeApplications}
              mode="multiple"
            >
              {applicationsList
                ?.filter((i) => i.active || selectedCA.has(i._id))
                .map((app) => (
                  <Option value={app._id} key={app._id}>
                    {capitalizeString(app.name)}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          {selectedCA.size === 0 ? (
            <p>Please select customer application first</p>
          ) : (
            <>
              <Form.Item name="dashboardsValue" label="Dashboards">
                <Select
                  mode="multiple"
                  placeholder="Select dashboards"
                  style={{ width: "100%" }}
                >
                  {displayedDashboards.map((dashboard) => (
                    <Option
                      value={getDashboardId(dashboard)}
                      key={getDashboardId(dashboard)}
                      title={dashboard.name}
                    >
                      {dashboard.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Roles"
                name="rolesValue"
                rules={[
                  {
                    required: true,
                    message: "Please select at least 1 role",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select roles"
                  style={{ width: "100%" }}
                  onChange={this.handleChangeRoles}
                >
                  {rolesList
                    ?.filter(
                      (r) =>
                        (selectedCA.has(normalizeId(r.customerApplication)) ||
                          (this.state.selectedRoles &&
                            Array.from(this.state.selectedRoles).includes(
                              normalizeId(r)
                            ))) &&
                        (r.isActive ||
                          (this.state.selectedRoles &&
                            Array.from(this.state.selectedRoles).includes(
                              normalizeId(r)
                            )))
                    )
                    .map((role) => (
                      <Option
                        value={normalizeId(role)}
                        key={normalizeId(role)}
                        title={role.description}
                      >
                        {capitalizeString(role.name)}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
              <Form.Item label="Permissions">
                {Array.from(selectedCA).map((ca) => {
                  const normalizedCa = normalizeId(ca);

                  if (!permissionMap.get(normalizedCa)) {
                    return null;
                  }
                  const [application] = applicationsList?.filter(
                    (a) => normalizeId(a) === normalizedCa
                  ),
                    list = permissionMap.get(normalizedCa)!;

                  const groupped = groupAndSortByKey(list, "category");

                  const renderedPermissions: React.ReactNode[] = [];
                  for (const [category, categoryList] of Object.entries(
                    groupped
                  )) {
                    const permissionGroup =
                      application && (
                        <Table
                          columns={this.columns}
                          dataSource={(categoryList as Permission[]).sort(
                            (a, b) => a.name.localeCompare(b.name)
                          )}
                          title={() => (
                            <Tooltip
                              title={`${capitalizeString(
                                category
                              )} category is part of ${capitalizeString(
                                application.name
                              )} customer application.`}
                              color={"var(--tertiary-color)"}
                              placement={"left"}
                            >
                              <span style={{ cursor: "help" }}>
                                {application.name}
                                <RightOutlined
                                  style={{
                                    color: "var(--primary-color)",
                                    margin: "0 3px",
                                    fontSize: "12px",
                                  }}
                                />
                                {category}
                              </span>
                            </Tooltip>
                          )}
                          key={`${application._id}-${category}`}
                          pagination={false}
                          className="role-table"
                        />
                      );

                    renderedPermissions.push(permissionGroup);
                  }

                  return renderedPermissions;
                })}
              </Form.Item>
            </>
          )}
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save User
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    );
  }
}

interface EditUserWrapperProps {
  handlers: EditUserHandlers;
  values: EditUserValues;
}

const EditUserWrapper: React.FC<EditUserWrapperProps> = ({ handlers, values }) => {
  useDrawerScrollMargin('edit-user-drawer', values.visibleEditDrawer);
  return <EditUser handlers={handlers} values={values} />
};

export default EditUserWrapper;
